import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import RecipeCard from '../components/RecipeCard';
import RecipeModalGateway from '../components/RecipeModalGateway';
import PremiumPurchaseModal from '../components/PremiumPurchaseModal';
import NotificationSelector from '../components/NotificationSelector';
import { API_BASE_URL } from '../config';
import { hydrateRecipeForModal } from '../utils/recipeModalData';
import specialDaysPin from '../assets/special-days-pin.png';

let googleMapsScriptPromise = null;

function loadGoogleMapsSdk(apiKey) {
  if (typeof window === 'undefined') return Promise.reject(new Error('Browser only'));
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleMapsScriptPromise) return googleMapsScriptPromise;

  googleMapsScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = (err) => {
      googleMapsScriptPromise = null;
      reject(err);
    };
    document.body.appendChild(script);
  });

  return googleMapsScriptPromise;
}

const EVENT_TYPES = [
  {
    value: 'birthday',
    label: 'Doğum Günü',
    description: 'Kutlama için tatlı, tuzlu ve meze dengeli menü önerileri.'
  },
  {
    value: 'gold_day',
    label: 'Altın Günü',
    description: 'Çay saati ve misafirlik için meze, tatlı ve ikramlık odaklı seçimler.'
  }
];

const SERVICE_OPTIONS = [
  { value: 'balanced', label: 'Dengeli' },
  { value: 'sweet', label: 'Tatlı Ağırlıklı' },
  { value: 'savory', label: 'Tuzlu Ağırlıklı' },
  { value: 'meze', label: 'Meze Ağırlıklı' }
];

const GUEST_DISH_FILTER_OPTIONS = [
  { value: 'sweet', label: 'Tatlı' },
  { value: 'savory', label: 'Tuzlu' },
  { value: 'meze', label: 'İkramlık' }
];

const MAIN_COURSE_FAMILIES = [
  { value: 'chicken', label: 'Tavuk' },
  { value: 'fish', label: 'Balık' },
  { value: 'meat', label: 'Et' },
  { value: 'legume', label: 'Bakliyat' }
];

const CITY_DISTRICT_OPTIONS = {
  'İstanbul': ['Adalar', 'Ataşehir', 'Avcılar', 'Bağcılar', 'Bahçelievler', 'Bakırköy', 'Başakşehir', 'Beşiktaş', 'Beylikdüzü', 'Beyoğlu', 'Büyükçekmece', 'Çekmeköy', 'Esenler', 'Esenyurt', 'Eyüpsultan', 'Fatih', 'Gaziosmanpaşa', 'Kadıköy', 'Kartal', 'Kağıthane', 'Küçükçekmece', 'Maltepe', 'Pendik', 'Sancaktepe', 'Sarıyer', 'Silivri', 'Şişli', 'Tuzla', 'Ümraniye', 'Üsküdar', 'Zeytinburnu'],
  'Ankara': ['Altındağ', 'Ayaş', 'Beypazarı', 'Çankaya', 'Etimesgut', 'Gölbaşı', 'Kahramankazan', 'Keçiören', 'Mamak', 'Polatlı', 'Pursaklar', 'Sincan', 'Yenimahalle'],
  'İzmir': ['Aliağa', 'Balçova', 'Bayraklı', 'Bornova', 'Buca', 'Çeşme', 'Dikili', 'Foça', 'Gaziemir', 'Güzelbahçe', 'Karabağlar', 'Karaburun', 'Karşıyaka', 'Kemalpaşa', 'Konak', 'Menderes', 'Menemen', 'Narlıdere', 'Seferihisar', 'Torbalı', 'Urla'],
  'Bursa': ['Gemlik', 'Gürsu', 'İnegöl', 'İznik', 'Karacabey', 'Mudanya', 'Mustafakemalpaşa', 'Nilüfer', 'Osmangazi', 'Yıldırım'],
  'Antalya': ['Aksu', 'Alanya', 'Döşemealtı', 'Finike', 'Kaş', 'Kemer', 'Kepez', 'Konyaaltı', 'Kumluca', 'Manavgat', 'Muratpaşa', 'Serik'],
  'Kocaeli': ['Başiskele', 'Çayırova', 'Darıca', 'Derince', 'Dilovası', 'Gebze', 'Gölcük', 'İzmit', 'Kandıra', 'Karamürsel', 'Kartepe', 'Körfez'],
  'Adana': ['Ceyhan', 'Çukurova', 'Feke', 'İmamoğlu', 'Karaisalı', 'Karataş', 'Kozan', 'Pozantı', 'Saimbeyli', 'Sarıçam', 'Seyhan', 'Tufanbeyli', 'Yumurtalık', 'Yüreğir'],
  'Konya': ['Akşehir', 'Beyşehir', 'Cihanbeyli', 'Ereğli', 'Ilgın', 'Karatay', 'Meram', 'Selçuklu', 'Seydişehir'],
  'Gaziantep': ['Araban', 'İslahiye', 'Karkamış', 'Nizip', 'Nurdağı', 'Oğuzeli', 'Şahinbey', 'Şehitkamil', 'Yavuzeli'],
  'Mersin': ['Akdeniz', 'Anamur', 'Aydıncık', 'Bozyazı', 'Çamlıyayla', 'Erdemli', 'Mezitli', 'Mut', 'Silifke', 'Tarsus', 'Toroslar', 'Yenişehir'],
  'Muğla': ['Bodrum', 'Dalaman', 'Datça', 'Fethiye', 'Kavaklıdere', 'Köyceğiz', 'Marmaris', 'Menteşe', 'Milas', 'Ortaca', 'Seydikemer', 'Ula', 'Yatağan'],
  'Eskişehir': ['Alpu', 'Beylikova', 'Çifteler', 'Mihalgazi', 'Odunpazarı', 'Sarıcakaya', 'Seyitgazi', 'Sivrihisar', 'Tepebaşı']
};

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const initialSetup = {
  eventType: 'birthday',
  guestCount: 8,
  serviceMode: 'snack',
  isMealHosted: false,
  serviceStyle: 'balanced',
  dishTypeFilters: ['sweet', 'savory'],
  mainCourseFamily: '',
  healthCheckEnabled: false,
  phone: '',
  city: '',
  district: ''
};

function getPlanningGuestCount(config) {
  const candidateValues = [
    config?.lunch?.servings,
    config?.dinner?.servings,
    config?.breakfast?.servings,
    config?.user?.servingSize
  ];

  for (const value of candidateValues) {
    const parsed = parseInt(String(value ?? '').replace(/[^\d]/g, ''), 10);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
}

const DEFAULT_MAP_CENTER = { lat: 39.0, lng: 35.0 };
const DEFAULT_MAP_ZOOM = 6;
const CITY_MAP_CENTERS = {
  antalya: { lat: 36.8969, lng: 30.7133, zoom: 11 },
  istanbul: { lat: 41.0082, lng: 28.9784, zoom: 10 },
  ankara: { lat: 39.9208, lng: 32.8541, zoom: 11 },
  izmir: { lat: 38.4237, lng: 27.1428, zoom: 11 }
};

const HEALTH_FLAG_LABELS = {
  gluten: 'Gluten',
  dairy: 'Süt Ürünleri',
  egg: 'Yumurta',
  nuts: 'Kuruyemiş'
};

function formatHealthFlags(flags) {
  if (!Array.isArray(flags) || !flags.length) return '-';
  const normalized = flags
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .map((key) => HEALTH_FLAG_LABELS[key] || key);
  return normalized.length ? normalized.join(', ') : '-';
}

function normalizeRecipeText(value) {
  return String(value || '')
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

function isSweetRecipe(recipe) {
  const source = [
    recipe?.serveStyle,
    recipe?.category,
    recipe?.canonical?.serveStyle,
    recipe?.canonical?.category,
    recipe?.name,
    recipe?.title
  ]
    .map(normalizeRecipeText)
    .join(' ');

  return ['tatli', 'tatlı', 'dessert', 'cake', 'pasta', 'kurabiye', 'sutlac', 'sütlaç', 'helva'].some((token) =>
    source.includes(normalizeRecipeText(token))
  );
}

function isMealRecipe(recipe) {
  const source = [
    recipe?.serveStyle,
    recipe?.category,
    recipe?.canonical?.serveStyle,
    recipe?.canonical?.category,
    recipe?.name,
    recipe?.title
  ]
    .map(normalizeRecipeText)
    .join(' ');

  return [
    'ana_yemek',
    'anayemek',
    'main',
    'corba',
    'çorba',
    'soup',
    'salata',
    'salad',
    'yan_yemek',
    'meze'
  ].some((token) => source.includes(normalizeRecipeText(token)));
}

function isSavoryRecipe(recipe) {
  if (isSweetRecipe(recipe)) return false;
  const source = [
    recipe?.serveStyle,
    recipe?.category,
    recipe?.canonical?.serveStyle,
    recipe?.canonical?.category,
    recipe?.name,
    recipe?.title
  ]
    .map(normalizeRecipeText)
    .join(' ');

  return ['tuzlu', 'savory', 'ana_yemek', 'anayemek', 'main', 'meze', 'corba', 'çorba', 'salata'].some((token) =>
    source.includes(normalizeRecipeText(token))
  );
}

function isMezeRecipe(recipe) {
  const source = [
    recipe?.serveStyle,
    recipe?.category,
    recipe?.canonical?.serveStyle,
    recipe?.canonical?.category,
    recipe?.name,
    recipe?.title
  ]
    .map(normalizeRecipeText)
    .join(' ');

  return ['meze', 'ikramlik', 'ikramlık', 'salata', 'appetizer', 'yan_yemek', 'atistirmalik', 'atıştırmalık'].some((token) =>
    source.includes(normalizeRecipeText(token))
  );
}

function normalizeDishTypeFilters(filters = []) {
  const allowed = new Set(['meal', 'sweet', 'savory', 'meze']);
  const mapped = (Array.isArray(filters) ? filters : [])
    .map((item) => String(item || '').trim().toLocaleLowerCase('tr-TR'))
    .map((item) => {
      if (item === 'yemek') return 'meal';
      if (item === 'tatli' || item === 'tatlı') return 'sweet';
      if (item === 'tuzlu') return 'savory';
      if (item === 'ikramlik' || item === 'ikramlık') return 'meze';
      return item;
    })
    .filter((item) => allowed.has(item));
  const unique = [...new Set(mapped)];
  return unique.length ? unique : ['sweet', 'savory'];
}

function buildDishTypeFiltersBySetup({
  serviceMode = 'snack',
  serviceStyle = 'balanced',
  dishTypeFilters = [],
  isMealHosted = false
} = {}) {
  const normalized = normalizeDishTypeFilters(dishTypeFilters);
  const mode = String(serviceMode || '').trim();
  const style = String(serviceStyle || 'balanced').trim();
  const mealMode = mode === 'meal' || mode === 'both' || !!isMealHosted;
  const snackMode = mode === 'snack' || mode === 'both' || !isMealHosted;

  if (mode === 'meal') return ['meal'];

  const inferredSnackFilters = (() => {
    if (style === 'sweet') return ['sweet'];
    if (style === 'savory') return ['savory'];
    if (style === 'meze') return ['meze'];
    return ['sweet', 'savory'];
  })();

  if (mode === 'snack') {
    const snackOnly = normalized.filter((item) => item !== 'meal');
    return snackOnly.length ? snackOnly : inferredSnackFilters;
  }

  if (mode === 'both') {
    const result = [];
    if (mealMode) result.push('meal');
    if (snackMode) {
      const snackOnly = normalized.filter((item) => item !== 'meal');
      result.push(...(snackOnly.length ? snackOnly : inferredSnackFilters));
    }
    return [...new Set(result)];
  }

  return normalized.length ? normalized : ['sweet', 'savory'];
}

function normalizeMapKey(value) {
  return String(value || '').trim().toLocaleLowerCase('tr-TR');
}

function buildAddressCandidates({ addressTitle, fullAddress, district, city }) {
  const title = String(addressTitle || '').trim();
  const address = String(fullAddress || '').trim();
  const town = String(district || '').trim();
  const province = String(city || '').trim();
  const candidates = [
    [address, town, province, 'Türkiye'],
    [title, address, town, province, 'Türkiye'],
    [address || title, town, province, 'Türkiye'],
    [address || title, province, 'Türkiye'],
    [address || title, town, province],
    [address || title, province]
  ].map((parts) => parts.filter(Boolean).join(', ').trim()).filter(Boolean);
  return [...new Set(candidates)];
}

export default function SpecialDaysKitchen({ isDarkMode, toggleDarkMode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [setup, setSetup] = useState(initialSetup);
  const [eventId, setEventId] = useState(null);
  const [setupCompleted, setSetupCompleted] = useState(false);
  const [invitePublished, setInvitePublished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipeIds, setSelectedRecipeIds] = useState([]);
  const [shoppingPreview, setShoppingPreview] = useState(null);
  const [shoppingLoading, setShoppingLoading] = useState(false);
  const [eventInventory, setEventInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inviteTemplates, setInviteTemplates] = useState([]);
  const [invitePreviewHtml, setInvitePreviewHtml] = useState('');
  const [invitePreviewLoading, setInvitePreviewLoading] = useState(false);
  const [surveyActiveDays, setSurveyActiveDays] = useState(3);
  const [surveyLink, setSurveyLink] = useState('');
  const [surveyCloseAt, setSurveyCloseAt] = useState(null);
  const [surveyCopied, setSurveyCopied] = useState(false);
  const [surveyLinkModalOpen, setSurveyLinkModalOpen] = useState(false);
  const [surveyRemainingMs, setSurveyRemainingMs] = useState(null);
  const [responseSummaryLoading, setResponseSummaryLoading] = useState(false);
  const [responseSummaryError, setResponseSummaryError] = useState('');
  const [responseAggregate, setResponseAggregate] = useState(null);
  const [inviteResponses, setInviteResponses] = useState([]);
  const [showResponseSummary, setShowResponseSummary] = useState(false);
  const [preferenceLoading, setPreferenceLoading] = useState(false);
  const [preferenceError, setPreferenceError] = useState('');
  const [preferenceLibraryOpen, setPreferenceLibraryOpen] = useState(false);
  const [recipeSearchTerm, setRecipeSearchTerm] = useState('');
  const [preferenceFilters, setPreferenceFilters] = useState({
    sweet: true,
    savory: true,
    meze: true
  });
  const [inviteForm, setInviteForm] = useState({
    templateId: '',
    useExistingAddress: true,
    hostName: '',
    eventTitle: '',
    eventDate: '',
    eventTime: '',
    addressTitle: '',
    fullAddress: '',
    city: '',
    district: '',
    note: '',
    healthCheckEnabled: false
  });
  const [inviteSaving, setInviteSaving] = useState(false);
  const [inviteSaveError, setInviteSaveError] = useState('');
  const [invitePublishStatus, setInvitePublishStatus] = useState(null);
  const [inviteStatusLoading, setInviteStatusLoading] = useState(false);
  const [hasSpecialDaysAccess, setHasSpecialDaysAccess] = useState(true);
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [pendingInvitePublish, setPendingInvitePublish] = useState(false);
  const [leftoverProcessing, setLeftoverProcessing] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeModalOpen, setRecipeModalOpen] = useState(false);
  const [shoppingScheduleModalOpen, setShoppingScheduleModalOpen] = useState(false);
  const [shoppingListSubmitting, setShoppingListSubmitting] = useState(false);
  const [shoppingListError, setShoppingListError] = useState('');
  const [shoppingReminder, setShoppingReminder] = useState({
    enabled: false,
    date: '',
    time: '09:00'
  });

  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [formattedAddress, setFormattedAddress] = useState('');
  const [locationConfirmed, setLocationConfirmed] = useState(false);
  const [isMapReady, setIsMapReady] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [mapError, setMapError] = useState('');
  const [mapConfigReady, setMapConfigReady] = useState(false);

  const mapDivRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const geocoderRef = useRef(null);
  const mapClickListenerRef = useRef(null);
  const mapApiKeyRef = useRef('');
  const preferenceLoadSignatureRef = useRef('');
  const publishAttemptInFlightRef = useRef(false);

  const activeEvent = useMemo(
    () => EVENT_TYPES.find((item) => item.value === setup.eventType) || EVENT_TYPES[0],
    [setup.eventType]
  );
  const shouldShowHealthColumn = useMemo(() => {
    if (!!setup.healthCheckEnabled) return true;
    if (Array.isArray(responseAggregate?.healthFlags) && responseAggregate.healthFlags.length > 0) return true;
    return inviteResponses.some((row) => Array.isArray(row?.healthFlags) && row.healthFlags.length > 0);
  }, [setup.healthCheckEnabled, responseAggregate?.healthFlags, inviteResponses]);
  const cityOptions = useMemo(() => Object.keys(CITY_DISTRICT_OPTIONS), []);
  const setupDistrictOptions = useMemo(() => CITY_DISTRICT_OPTIONS[setup.city] || [], [setup.city]);
  const inviteDistrictOptions = useMemo(() => CITY_DISTRICT_OPTIONS[inviteForm.city] || [], [inviteForm.city]);
  const preferredMapCenter = useMemo(() => {
    const city = normalizeMapKey(inviteForm.city || setup.city);
    if (city && CITY_MAP_CENTERS[city]) return CITY_MAP_CENTERS[city];
    return { ...DEFAULT_MAP_CENTER, zoom: DEFAULT_MAP_ZOOM };
  }, [inviteForm.city, setup.city]);
  const filteredPreferenceRecipes = useMemo(() => {
    const search = normalizeRecipeText(recipeSearchTerm);
    const mealFilterActive = false;
    const hasAnyActiveFilter = preferenceFilters.sweet || preferenceFilters.savory || preferenceFilters.meze;

    return recipes.filter((recipe) => {
      const matchesSearch = !search || normalizeRecipeText(`${recipe?.name || recipe?.title || ''}`).includes(search);
      if (!matchesSearch) return false;
      if (!hasAnyActiveFilter) return true;

      const matchesMeal = false;
      const matchesSweet = preferenceFilters.sweet && isSweetRecipe(recipe);
      const matchesSavory = preferenceFilters.savory && isSavoryRecipe(recipe);
      const matchesMeze = preferenceFilters.meze && isMezeRecipe(recipe);

      return matchesMeal || matchesSweet || matchesSavory || matchesMeze;
    });
  }, [recipes, recipeSearchTerm, preferenceFilters]);

  const loadProfileContact = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) return;
      const { data } = await axios.get(`${API_BASE_URL}/api/special-days/profile-contact`, { headers });

      const phone = String(data?.contact?.phone || '').trim();
      const city = String(data?.contact?.city || '').trim();
      const district = String(data?.contact?.district || '').trim();

      setSetup((prev) => ({
        ...prev,
        phone: prev.phone || phone,
        city: prev.city || city,
        district: prev.district || district
      }));
    } catch (error) {
      console.error('Special days profile contact prefill error:', error);
    }
  }, []);

  useEffect(() => {
    const prefill = location.state?.prefill;
    if (!prefill) return;

    setSetup((prev) => ({
      ...prev,
      eventType: prefill.eventType || prev.eventType,
      healthCheckEnabled:
        typeof prefill.healthCheckEnabled === 'boolean'
          ? prefill.healthCheckEnabled
          : prev.healthCheckEnabled
    }));
  }, [location.state]);

  useEffect(() => {
    loadProfileContact();
  }, [loadProfileContact]);

  useEffect(() => {
    const handleProfileUpdated = () => {
      loadProfileContact();
    };

    window.addEventListener('profileUpdated', handleProfileUpdated);
    return () => window.removeEventListener('profileUpdated', handleProfileUpdated);
  }, [loadProfileContact]);

  useEffect(() => {
    let mounted = true;

    const restoreLatestEvent = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers.Authorization) return;

        const { data } = await axios.get(`${API_BASE_URL}/api/special-days/events/latest`, { headers });
        if (!mounted) return;

        const latestEvent = data?.event;
        if (!latestEvent?.id) return;

        const setupData = latestEvent.setupData && typeof latestEvent.setupData === 'object' ? latestEvent.setupData : {};
        const inviteData = latestEvent.inviteData && typeof latestEvent.inviteData === 'object' ? latestEvent.inviteData : {};
        const locationData = inviteData.location && typeof inviteData.location === 'object' ? inviteData.location : {};

        setEventId(Number(latestEvent.id));
        setSetup((prev) => ({
          ...prev,
          eventType: String(latestEvent.eventType || prev.eventType),
          guestCount: Number(setupData.guestCount || prev.guestCount || 8),
          serviceMode: String(setupData.serviceMode || prev.serviceMode || 'snack'),
          isMealHosted: !!setupData.isMealHosted,
          serviceStyle: String(setupData.serviceStyle || prev.serviceStyle || 'balanced'),
          dishTypeFilters: buildDishTypeFiltersBySetup({
            serviceMode: String(setupData.serviceMode || prev.serviceMode || 'snack'),
            isMealHosted: !!setupData.isMealHosted,
            serviceStyle: String(setupData.serviceStyle || prev.serviceStyle || 'balanced'),
            dishTypeFilters: setupData.dishTypeFilters || prev.dishTypeFilters
          }),
          mainCourseFamily: String(setupData.mainCourseFamily || prev.mainCourseFamily || ''),
          healthCheckEnabled:
            typeof setupData.healthCheckEnabled === 'boolean'
              ? setupData.healthCheckEnabled
              : prev.healthCheckEnabled,
          phone: String(setupData.phone || prev.phone || ''),
          city: String(setupData.city || prev.city || ''),
          district: String(setupData.district || prev.district || '')
        }));
        setSetupCompleted(true);

        setInviteForm((prev) => ({
          ...prev,
          templateId: String(inviteData.templateId || prev.templateId || ''),
          hostName: String(inviteData.hostName || prev.hostName || ''),
          eventTitle: String(inviteData.eventTitle || prev.eventTitle || ''),
          eventDate: String(inviteData.eventDate || prev.eventDate || ''),
          eventTime: String(inviteData.eventTime || prev.eventTime || ''),
          addressTitle: String(inviteData.addressTitle || prev.addressTitle || ''),
          fullAddress: String(inviteData.fullAddress || prev.fullAddress || ''),
          city: String(inviteData.city || setupData.city || prev.city || ''),
          district: String(inviteData.district || setupData.district || prev.district || ''),
          note: String(inviteData.note || prev.note || ''),
          healthCheckEnabled:
            typeof inviteData.healthCheckEnabled === 'boolean'
              ? inviteData.healthCheckEnabled
              : prev.healthCheckEnabled
        }));

        const latFromInvite = Number(locationData.lat ?? inviteData.mapLat);
        const lngFromInvite = Number(locationData.lng ?? inviteData.mapLng);
        if (Number.isFinite(latFromInvite) && Number.isFinite(lngFromInvite)) {
          setLat(latFromInvite);
          setLng(lngFromInvite);
        }
        if (locationData.formattedAddress) {
          setFormattedAddress(String(locationData.formattedAddress));
        }
        setLocationConfirmed(!!locationData.isConfirmed || !!inviteData.mapConfirmed);
        setInvitePublished(
          !!inviteData.publishedAt ||
          String(inviteData.workflowState || '') === 'invitation_published' ||
          ['open', 'closed'].includes(String(inviteData.surveyStatus || '').toLowerCase())
        );

        if (inviteData.surveyCloseAt) {
          setSurveyCloseAt(inviteData.surveyCloseAt);
        }
        if (Number.isFinite(Number(inviteData.surveyActiveDays))) {
          setSurveyActiveDays(Number(inviteData.surveyActiveDays));
        }

        try {
          const statusRes = await axios.get(
            `${API_BASE_URL}/api/special-days/events/${latestEvent.id}/publish-status`,
            { headers }
          );
          if (mounted) {
            setInvitePublishStatus(statusRes?.data || null);
            if (typeof statusRes?.data?.access?.hasAccess === 'boolean') {
              setHasSpecialDaysAccess(!!statusRes.data.access.hasAccess);
            }
          }
        } catch (statusErr) {
          console.error('Special days latest publish-status restore error:', statusErr);
        }

        const shouldOpenSummary =
          !!inviteData.inviteToken ||
          String(inviteData.surveyStatus || '').toLowerCase() === 'closed' ||
          String(latestEvent.status || '').toLowerCase() === 'awaiting_responses';

        if (shouldOpenSummary) {
          const summaryRes = await axios.get(
            `${API_BASE_URL}/api/special-days/events/${latestEvent.id}/response-summary`,
            { headers }
          );
          if (!mounted) return;
          const summaryData = summaryRes?.data || {};
          setResponseAggregate(summaryData?.aggregate || null);
          setInviteResponses(Array.isArray(summaryData?.responses) ? summaryData.responses : []);
          if (summaryData?.survey?.closeAt) setSurveyCloseAt(summaryData.survey.closeAt);
          if (Number.isFinite(Number(summaryData?.survey?.activeDays))) {
            setSurveyActiveDays(Number(summaryData.survey.activeDays));
          }
          setShowResponseSummary(true);
        }
      } catch (error) {
        console.error('Special days latest event restore error:', error);
      }
    };

    restoreLatestEvent();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const fetchPlanningDefaults = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers.Authorization) return;
        const { data } = await axios.get(`${API_BASE_URL}/api/planning/config`, { headers });
        const planningGuestCount = getPlanningGuestCount(data?.config);
        if (!planningGuestCount) return;

        setSetup((prev) => ({
          ...prev,
          guestCount: planningGuestCount
        }));
      } catch (error) {
        console.error('Special days planning prefill error:', error);
      }
    };

    fetchPlanningDefaults();
  }, []);

  useEffect(() => {
    if (!setupCompleted) return;

    const loadTemplates = async () => {
      try {
        const headers = getAuthHeaders();
        const { data } = await axios.get(`${API_BASE_URL}/api/special-days/invite/templates`, {
          headers,
          params: { eventType: setup.eventType }
        });

        const templates = Array.isArray(data?.templates) ? data.templates : [];
        setInviteTemplates(templates);

        setInviteForm((prev) => {
          const nextTemplateId = prev.templateId || templates[0]?.id || '';
          return {
            ...prev,
            templateId: nextTemplateId,
            eventTitle:
              prev.eventTitle ||
              (setup.eventType === 'birthday' ? 'Doğum Günü Daveti' : 'Altın Günü Daveti'),
            healthCheckEnabled: setup.healthCheckEnabled,
            city: prev.city || setup.city.trim(),
            district: prev.district || setup.district.trim()
          };
        });
      } catch (error) {
        console.error('Special days invite templates error:', error);
        setInviteTemplates([]);
      }
    };

    loadTemplates();
  }, [setupCompleted, setup.eventType, setup.healthCheckEnabled, setup.city, setup.district]);

  useEffect(() => {
    let isActive = true;
    if (!setupCompleted) return undefined;

    const fetchMapConfig = async () => {
      try {
        const headers = getAuthHeaders();
        if (!headers.Authorization) return;
        const { data } = await axios.get(`${API_BASE_URL}/api/special-days/map-config`, { headers });
        if (!isActive) return;
        mapApiKeyRef.current = String(data?.apiKey || '').trim();
        if (!mapApiKeyRef.current) {
          setMapError('Harita servisi geçici olarak kullanılamıyor.');
        }
      } catch (error) {
        if (!isActive) return;
        console.error('Special days map config error:', error);
        setMapError('Harita ayarları alınamadı. Lütfen tekrar deneyin.');
      } finally {
        if (isActive) setMapConfigReady(true);
      }
    };

    fetchMapConfig();
    return () => {
      isActive = false;
    };
  }, [setupCompleted]);

  const reverseGeocode = useCallback(async (maps, latitude, longitude) => {
    try {
      if (!geocoderRef.current) {
        geocoderRef.current = new maps.Geocoder();
      }
      const results = await geocoderRef.current.geocode({ location: { lat: latitude, lng: longitude } });
      setFormattedAddress(results?.results?.[0]?.formatted_address || '');
    } catch (_err) {
      setFormattedAddress('');
    }
  }, []);

  const ensureMapReady = useCallback(async (center = DEFAULT_MAP_CENTER, zoom = DEFAULT_MAP_ZOOM) => {
    if (!mapDivRef.current) return null;

    const apiKey = String(mapApiKeyRef.current || '').trim();
    if (!apiKey) {
      setMapError('Harita için API anahtarı bulunamadı.');
      return null;
    }

    const maps = await loadGoogleMapsSdk(apiKey);

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new maps.Map(mapDivRef.current, {
        center,
        zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false
      });
    } else {
      mapInstanceRef.current.setCenter(center);
      if (Number.isFinite(zoom)) mapInstanceRef.current.setZoom(zoom);
    }

    if (!mapClickListenerRef.current) {
      mapClickListenerRef.current = mapInstanceRef.current.addListener('click', async (event) => {
        const clickLat = event?.latLng?.lat?.();
        const clickLng = event?.latLng?.lng?.();
        if (!Number.isFinite(clickLat) || !Number.isFinite(clickLng)) return;
        setLat(clickLat);
        setLng(clickLng);
        setLocationConfirmed(false);
        setMapError('');
        if (markerRef.current) markerRef.current.setPosition({ lat: clickLat, lng: clickLng });
        await reverseGeocode(maps, clickLat, clickLng);
      });
    }

    setIsMapReady(true);
    return { maps, map: mapInstanceRef.current };
  }, [reverseGeocode]);

  const updateMarker = useCallback(async ({ latitude, longitude, centerMap = true } = {}) => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
    const ready = await ensureMapReady({ lat: latitude, lng: longitude }, 16);
    if (!ready) return;

    const { maps, map } = ready;
    if (!markerRef.current) {
      markerRef.current = new maps.Marker({
        map,
        position: { lat: latitude, lng: longitude },
        draggable: true,
        icon: {
          url: specialDaysPin,
          scaledSize: new maps.Size(42, 42)
        }
      });

      markerRef.current.addListener('dragend', async (event) => {
        const dragLat = event?.latLng?.lat?.();
        const dragLng = event?.latLng?.lng?.();
        if (!Number.isFinite(dragLat) || !Number.isFinite(dragLng)) return;
        setLat(dragLat);
        setLng(dragLng);
        setLocationConfirmed(false);
        setMapError('');
        await reverseGeocode(maps, dragLat, dragLng);
      });
    } else {
      markerRef.current.setMap(map);
      markerRef.current.setPosition({ lat: latitude, lng: longitude });
    }

    if (centerMap) {
      map.panTo({ lat: latitude, lng: longitude });
      if ((map.getZoom?.() || 0) < 15) map.setZoom(16);
    }
  }, [ensureMapReady, reverseGeocode]);

  const handlePinAction = async () => {
    setMapError('');
    setIsGeocoding(true);

    const district = String(inviteForm.district || setup.district || '').trim();
    const city = String(inviteForm.city || setup.city || '').trim();
    const addressCandidates = buildAddressCandidates({
      addressTitle: inviteForm.addressTitle,
      fullAddress: inviteForm.fullAddress,
      district,
      city
    });

    const applyGeocodeResult = async ({ latitude, longitude, label }) => {
      setLat(latitude);
      setLng(longitude);
      setFormattedAddress(String(label || '').trim());
      setLocationConfirmed(false);
      await updateMarker({ latitude, longitude, centerMap: true });
    };

    const fallbackToManualMode = async () => {
      await ensureMapReady(
        { lat: preferredMapCenter.lat, lng: preferredMapCenter.lng },
        preferredMapCenter.zoom || DEFAULT_MAP_ZOOM
      );
      await updateMarker({
        latitude: preferredMapCenter.lat,
        longitude: preferredMapCenter.lng,
        centerMap: true
      });
      setMapError('Adres otomatik bulunamadı. Haritada pini elle taşıyarak seçebilirsin.');
    };

    const tryClientSideGeocode = async () => {
      const ready = await ensureMapReady(
        { lat: preferredMapCenter.lat, lng: preferredMapCenter.lng },
        preferredMapCenter.zoom || DEFAULT_MAP_ZOOM
      );
      if (!ready) return false;
      const { maps } = ready;
      if (!geocoderRef.current) geocoderRef.current = new maps.Geocoder();

      for (const candidate of addressCandidates) {
        // eslint-disable-next-line no-await-in-loop
        const response = await geocoderRef.current.geocode({
          address: candidate,
          region: 'TR',
          componentRestrictions: { country: 'TR' }
        });
        const first = response?.results?.[0];
        const gLoc = first?.geometry?.location;
        const latitude = Number(gLoc?.lat?.());
        const longitude = Number(gLoc?.lng?.());
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
          await applyGeocodeResult({
            latitude,
            longitude,
            label: first?.formatted_address || candidate
          });
          return true;
        }
      }

      return false;
    };

    try {
      const clientFirstOk = await tryClientSideGeocode();
      if (clientFirstOk) return;

      const headers = getAuthHeaders();
      const { data } = await axios.post(
        `${API_BASE_URL}/api/special-days/geocode-preview`,
        {
          addressTitle: inviteForm.addressTitle,
          fullAddress: inviteForm.fullAddress,
          district,
          city
        },
        { headers }
      );

      const nextLat = Number(data?.lat);
      const nextLng = Number(data?.lng);
      if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) {
        throw new Error('Adres için koordinat bulunamadı.');
      }

      await applyGeocodeResult({
        latitude: nextLat,
        longitude: nextLng,
        label: data?.formattedAddress || data?.label
      });
    } catch (error) {
      console.error('Special days pin action error:', error);
      try {
        const fallbackOk = await tryClientSideGeocode();
        if (!fallbackOk) await fallbackToManualMode();
      } catch (fallbackError) {
        console.error('Special days client geocode error:', fallbackError);
        await fallbackToManualMode();
      }
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleConfirmLocation = () => {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setMapError('Lütfen önce geçerli bir pin yerleştirin.');
      return;
    }
    setLocationConfirmed(true);
    setMapError('');
  };

  const handleResetLocation = () => {
    setLat(null);
    setLng(null);
    setFormattedAddress('');
    setLocationConfirmed(false);
    setMapError('');
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setCenter({ lat: preferredMapCenter.lat, lng: preferredMapCenter.lng });
      mapInstanceRef.current.setZoom(preferredMapCenter.zoom || DEFAULT_MAP_ZOOM);
    }
  };

  useEffect(() => {
    return () => {
      if (mapClickListenerRef.current) {
        mapClickListenerRef.current.remove();
        mapClickListenerRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!setupCompleted || !mapConfigReady || !mapApiKeyRef.current) return undefined;

    const initMap = async () => {
      try {
        const center = Number.isFinite(lat) && Number.isFinite(lng)
          ? { lat, lng }
          : { lat: preferredMapCenter.lat, lng: preferredMapCenter.lng };

        await ensureMapReady(
          center,
          Number.isFinite(lat) && Number.isFinite(lng)
            ? 15
            : (preferredMapCenter.zoom || DEFAULT_MAP_ZOOM)
        );

        if (cancelled) return;
        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          await updateMarker({ latitude: lat, longitude: lng, centerMap: false });
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Special days map init error:', error);
        setMapError('Harita başlatılamadı. Lütfen sayfayı yenileyip tekrar deneyin.');
      }
    };

    initMap();
    return () => {
      cancelled = true;
    };
  }, [setupCompleted, mapConfigReady, lat, lng, preferredMapCenter, ensureMapReady, updateMarker]);

  useEffect(() => {
    let cancelled = false;
    if (!setupCompleted || !mapConfigReady || !mapApiKeyRef.current) return undefined;
    if (locationConfirmed) return undefined;

    const district = String(inviteForm.district || setup.district || '').trim();
    const city = String(inviteForm.city || setup.city || '').trim();
    const candidates = buildAddressCandidates({
      addressTitle: inviteForm.addressTitle,
      fullAddress: inviteForm.fullAddress,
      district,
      city
    });

    if (!candidates.length) return undefined;

    const timer = setTimeout(async () => {
      try {
        const ready = await ensureMapReady(
          { lat: preferredMapCenter.lat, lng: preferredMapCenter.lng },
          preferredMapCenter.zoom || DEFAULT_MAP_ZOOM
        );
        if (!ready || cancelled) return;

        const { maps } = ready;
        if (!geocoderRef.current) geocoderRef.current = new maps.Geocoder();

        for (const candidate of candidates) {
          // eslint-disable-next-line no-await-in-loop
          const response = await geocoderRef.current.geocode({
            address: candidate,
            region: 'TR',
            componentRestrictions: { country: 'TR' }
          });
          if (cancelled) return;

          const first = response?.results?.[0];
          const gLoc = first?.geometry?.location;
          const latitude = Number(gLoc?.lat?.());
          const longitude = Number(gLoc?.lng?.());

          if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            setLat(latitude);
            setLng(longitude);
            setFormattedAddress(first?.formatted_address || candidate);
            await updateMarker({ latitude, longitude, centerMap: true });
            break;
          }
        }
      } catch (_error) {
        // Yazarken geocode başarısız olursa sessizce geç; kullanıcı Pin Koy ile tekrar deneyebilir.
      }
    }, 500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    setupCompleted,
    mapConfigReady,
    locationConfirmed,
    inviteForm.addressTitle,
    inviteForm.fullAddress,
    inviteForm.city,
    inviteForm.district,
    setup.city,
    setup.district,
    preferredMapCenter,
    ensureMapReady,
    updateMarker
  ]);

  const handleChange = (field, value) => {
    setSetup((prev) => {
      if (field === 'dishTypeFilters') {
        const current = buildDishTypeFiltersBySetup({
          serviceMode: prev.serviceMode,
          isMealHosted: prev.isMealHosted,
          serviceStyle: prev.serviceStyle,
          dishTypeFilters: prev.dishTypeFilters
        });
        const next = current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value];
        return {
          ...prev,
          dishTypeFilters: buildDishTypeFiltersBySetup({
            serviceMode: prev.serviceMode,
            isMealHosted: prev.isMealHosted,
            serviceStyle: prev.serviceStyle,
            dishTypeFilters: next.length ? next : current
          })
        };
      }

      if (field === 'city') {
        return {
          ...prev,
          city: value,
          district: ''
        };
      }

      if (field === 'serviceMode') {
        return {
          ...prev,
          serviceMode: value,
          isMealHosted: value === 'meal' || value === 'both',
          serviceStyle: value === 'meal' ? prev.serviceStyle : (prev.serviceStyle || 'balanced'),
          dishTypeFilters: buildDishTypeFiltersBySetup({
            serviceMode: value,
            isMealHosted: value === 'meal' || value === 'both',
            serviceStyle: value === 'meal' ? prev.serviceStyle : (prev.serviceStyle || 'balanced'),
            dishTypeFilters: prev.dishTypeFilters
          }),
          mainCourseFamily: value === 'snack' ? '' : prev.mainCourseFamily
        };
      }

      return {
        ...prev,
        [field]: value,
        ...(field === 'serviceStyle'
          ? {
            dishTypeFilters: buildDishTypeFiltersBySetup({
              serviceMode: prev.serviceMode,
              isMealHosted: prev.isMealHosted,
              serviceStyle: value,
              dishTypeFilters: prev.dishTypeFilters
            })
          }
          : {})
      };
    });
  };

  const handleSetupSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.post(
        `${API_BASE_URL}/api/special-days/setup-preview`,
        {
          eventId,
          eventType: setup.eventType,
          guestCount: Number(setup.guestCount || 0),
          serviceMode: setup.serviceMode,
          isMealHosted: !!setup.isMealHosted,
          serviceStyle: setup.serviceStyle,
          dishTypeFilters: buildDishTypeFiltersBySetup({
            serviceMode: setup.serviceMode,
            isMealHosted: !!setup.isMealHosted,
            serviceStyle: setup.serviceStyle,
            dishTypeFilters: setup.dishTypeFilters
          }),
          mainCourseFamily: setup.mainCourseFamily,
          healthCheckEnabled: !!setup.healthCheckEnabled,
          phone: setup.phone.trim(),
          city: setup.city.trim(),
          district: setup.district.trim()
        },
        { headers }
      );

      setEventId(data?.event?.id || null);
      setRecipes(Array.isArray(data?.recipes) ? data.recipes : []);
      setSelectedRecipeIds([]);
      setShoppingPreview(null);
      setEventInventory([]);
      setInvitePublished(false);
      setInviteForm((prev) => ({
        ...prev,
        city: setup.city.trim(),
        district: setup.district.trim()
      }));
      setSetupCompleted(true);
      if (data?.event?.id) {
        await refreshEventInventory(data.event.id);
      }
    } catch (error) {
      console.error('Special days preview error:', error);
      const statusCode = Number(error?.response?.status || 0);
      // Erişim/ödeme kapısı: sessiz boş menü yerine satın alma modalını aç (beklenen davranış: ilk formda ödeme iste).
      if (statusCode === 403) {
        setHasSpecialDaysAccess(false);
        setPurchaseModalOpen(true);
        return;
      }
      setRecipes([]);
      setSetupCompleted(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRecipeClick = async (recipe) => {
    const hydrated = await hydrateRecipeForModal(recipe);
    setSelectedRecipe(hydrated);
    setRecipeModalOpen(true);
  };

  const refreshEventInventory = async (targetEventId = eventId) => {
    if (!targetEventId) return;
    setInventoryLoading(true);
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.get(`${API_BASE_URL}/api/special-days/events/${targetEventId}/inventory`, {
        headers
      });
      setEventInventory(Array.isArray(data?.inventory) ? data.inventory : []);
    } catch (error) {
      console.error('Special days inventory fetch error:', error);
      setEventInventory([]);
    } finally {
      setInventoryLoading(false);
    }
  };

  const toggleRecipeSelection = (recipe) => {
    const recipeId = String(recipe?.id || '');
    if (!recipeId) return;

    setSelectedRecipeIds((prev) =>
      prev.includes(recipeId) ? prev.filter((id) => id !== recipeId) : [...prev, recipeId]
    );
  };

  const handleModalRecipeSelect = (recipeId) => {
    const normalizedId = String(recipeId || '');
    if (!normalizedId) return;
    setSelectedRecipeIds((prev) =>
      prev.includes(normalizedId) ? prev.filter((id) => id !== normalizedId) : [...prev, normalizedId]
    );
  };

  const handleShoppingPreview = async () => {
    if (!eventId || !selectedRecipeIds.length) return;

    setShoppingLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API_BASE_URL}/api/special-days/events/${eventId}/selections`,
        { selectedRecipeIds },
        { headers }
      );

      const { data } = await axios.get(`${API_BASE_URL}/api/special-days/events/${eventId}/shopping-preview`, {
        headers
      });

      setShoppingPreview(data || null);
    } catch (error) {
      console.error('Special days shopping preview error:', error);
      setShoppingPreview(null);
    } finally {
      setShoppingLoading(false);
    }
  };

  const handleAddMissingToEventInventory = async () => {
    if (!eventId || !shoppingPreview?.missingItems?.length) return;
    setInventoryLoading(true);
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.post(
        `${API_BASE_URL}/api/special-days/events/${eventId}/inventory/add`,
        {
          items: shoppingPreview.missingItems
        },
        { headers }
      );

      setEventInventory(Array.isArray(data?.inventory) ? data.inventory : []);
    } catch (error) {
      console.error('Special days inventory add error:', error);
    } finally {
      setInventoryLoading(false);
    }
  };

  const handleOpenShoppingScheduleModal = () => {
    if (!eventId) return;
    if (!shoppingPreview?.missingItems?.length) {
      setShoppingListError('Önce eksik malzeme listesini çıkarın.');
      return;
    }
    setShoppingListError('');
    setShoppingScheduleModalOpen(true);
  };

  const handleCreateShoppingList = async () => {
    if (!eventId) return;
    if (!shoppingPreview?.missingItems?.length) {
      setShoppingListError('Eksik malzeme bulunamadı.');
      return;
    }
    if (!shoppingReminder?.enabled || !shoppingReminder?.date || !shoppingReminder?.time) {
      setShoppingListError('Lütfen alışveriş tarihi ve saatini teyit edin.');
      return;
    }

    setShoppingListSubmitting(true);
    setShoppingListError('');
    try {
      const headers = getAuthHeaders();
      await axios.post(
        `${API_BASE_URL}/api/special-days/events/${eventId}/shopping-list`,
        {
          notification: shoppingReminder
        },
        { headers }
      );

      setShoppingScheduleModalOpen(false);
      navigate('/my-shopping-lists');
    } catch (error) {
      const message = error?.response?.data?.message || 'Alışveriş listesi oluşturulamadı.';
      setShoppingListError(message);
    } finally {
      setShoppingListSubmitting(false);
    }
  };

  const handleMarkRecipeAsUsed = async (recipe) => {
    if (!eventId || !recipe?.id) return;
    const headers = getAuthHeaders();
    await axios.post(
      `${API_BASE_URL}/api/special-days/events/${eventId}/consume-recipe`,
      { recipeId: recipe.id },
      { headers }
    );
    await refreshEventInventory(eventId);
  };

  const handleInviteChange = (field, value) => {
    if (['addressTitle', 'fullAddress', 'city', 'district', 'useExistingAddress'].includes(field)) {
      setLocationConfirmed(false);
    }

    setInviteForm((prev) => {
      if (field === 'city') {
        return {
          ...prev,
          city: value,
          district: ''
        };
      }

      if (field === 'isMealHosted') {
        const nextValue = !!value;
        return {
          ...prev,
          isMealHosted: nextValue,
          serviceStyle: nextValue ? '' : (prev.serviceStyle || 'balanced'),
          mainCourseFamily: nextValue ? prev.mainCourseFamily : ''
        };
      }

      return {
        ...prev,
        [field]: value
      };
    });
  };

  const buildInvitePayload = () => ({
    ...inviteForm,
    surveyActiveDays,
    location: {
      addressText: String(inviteForm.fullAddress || inviteForm.addressTitle || '').trim(),
      formattedAddress: String(formattedAddress || '').trim(),
      lat: Number.isFinite(lat) ? lat : null,
      lng: Number.isFinite(lng) ? lng : null,
      isConfirmed: !!locationConfirmed && Number.isFinite(lat) && Number.isFinite(lng)
    },
    mapLat: Number.isFinite(lat) ? lat : null,
    mapLng: Number.isFinite(lng) ? lng : null,
    mapConfirmed: !!locationConfirmed && Number.isFinite(lat) && Number.isFinite(lng)
  });

  const refreshInvitationStatus = useCallback(async (targetEventId = eventId) => {
    if (!targetEventId) return null;
    setInviteStatusLoading(true);
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.get(`${API_BASE_URL}/api/special-days/events/${targetEventId}/publish-status`, { headers });
      setInvitePublishStatus(data || null);
      if (typeof data?.access?.hasAccess === 'boolean') {
        setHasSpecialDaysAccess(!!data.access.hasAccess);
      }
      return data || null;
    } catch (error) {
      console.error('Special days publish-status error:', error);
      return null;
    } finally {
      setInviteStatusLoading(false);
    }
  }, [eventId]);

  const refreshSpecialDaysAccess = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.get(`${API_BASE_URL}/api/special-days/access-status`, { headers });
      const allowed = !!data?.hasAccess;
      setHasSpecialDaysAccess(allowed);
      return allowed;
    } catch (error) {
      console.error('Special days access status error:', error);
      setHasSpecialDaysAccess(false);
      return false;
    }
  }, []);

  useEffect(() => {
    if (!setupCompleted) return;
    refreshSpecialDaysAccess();
  }, [setupCompleted, refreshSpecialDaysAccess]);

  useEffect(() => {
    if (!eventId) {
      setInvitePublishStatus(null);
      return;
    }
    refreshInvitationStatus(eventId);
  }, [eventId, refreshInvitationStatus]);

  useEffect(() => {
    if (!hasSpecialDaysAccess) return;
    if (purchaseModalOpen) setPurchaseModalOpen(false);
  }, [hasSpecialDaysAccess, purchaseModalOpen]);

  useEffect(() => {
    if (!eventId || !inviteForm.templateId) return undefined;
    if (!locationConfirmed || !Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setInvitePreviewLoading(true);
      try {
        const headers = getAuthHeaders();
        const { data } = await axios.post(
          `${API_BASE_URL}/api/special-days/events/${eventId}/invite/preview`,
          buildInvitePayload(),
          { headers }
        );
        if (!cancelled) {
          setInvitePreviewHtml(data?.html || '');
        }
      } catch (error) {
        console.error('Special days invite auto-preview error:', error);
        if (!cancelled) setInvitePreviewHtml('');
      } finally {
        if (!cancelled) setInvitePreviewLoading(false);
      }
    }, 450);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    eventId,
    inviteForm.templateId,
    inviteForm.hostName,
    inviteForm.eventTitle,
    inviteForm.eventDate,
    inviteForm.eventTime,
    inviteForm.addressTitle,
    inviteForm.fullAddress,
    inviteForm.city,
    inviteForm.district,
    inviteForm.note,
    locationConfirmed,
    lat,
    lng,
    formattedAddress
  ]);

  const saveInviteDraft = useCallback(async () => {
    if (!eventId || !inviteForm.templateId) return;
    setInviteSaveError('');
    setInviteSaving(true);
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.post(
        `${API_BASE_URL}/api/special-days/events/${eventId}/invitation/save-draft`,
        buildInvitePayload(),
        { headers }
      );
      setInvitePublished(false);
      setSurveyLink(String(data?.surveyLink || ''));
      await refreshInvitationStatus(eventId);
    } catch (error) {
      console.error('Special days invitation save-draft error:', error);
      const message = error?.response?.data?.message || 'Taslak kaydedilemedi. Lütfen tekrar deneyin.';
      setInviteSaveError(String(message));
    } finally {
      setInviteSaving(false);
    }
  }, [eventId, inviteForm.templateId, buildInvitePayload, refreshInvitationStatus]);

  const publishInvitation = useCallback(async () => {
    if (!eventId || !inviteForm.templateId) return;
    setInviteSaveError('');
    setInviteSaving(true);
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.post(
        `${API_BASE_URL}/api/special-days/events/${eventId}/invitation/publish`,
        buildInvitePayload(),
        { headers }
      );

      setSurveyLink(String(data?.surveyLink || ''));
      setSurveyCloseAt(data?.surveyCloseAt || null);
      setSurveyLinkModalOpen(!!data?.surveyLink);
      setInvitePublished(true);
      setPendingInvitePublish(false);
      await refreshInvitationStatus(eventId);
    } catch (error) {
      console.error('Special days invitation publish error:', error);
      const statusCode = Number(error?.response?.status || 0);
      const apiCode = String(error?.response?.data?.code || '');
      const message = error?.response?.data?.message || 'Davetiye yayınlanamadı.';

      if (statusCode === 403 && apiCode === 'PAYMENT_REQUIRED') {
        setPendingInvitePublish(true);
        setPurchaseModalOpen(true);
        setInviteSaveError('Ödeme onayı tamamlanınca davetiye otomatik yayınlanacak.');
      } else {
        setInviteSaveError(String(message));
      }
      await refreshInvitationStatus(eventId);
    } finally {
      setInviteSaving(false);
    }
  }, [eventId, inviteForm.templateId, buildInvitePayload, refreshInvitationStatus]);

  const tryAutoPublishAfterPayment = useCallback(async () => {
    if (!pendingInvitePublish || !eventId || publishAttemptInFlightRef.current) return false;
    const status = await refreshInvitationStatus(eventId);
    const hasAccess = !!status?.access?.hasAccess;
    const paymentState = String(status?.payment?.state || 'none');

    if (!hasAccess) {
      if (paymentState === 'expired') {
        setInviteSaveError('Ödeme süresi doldu. Lütfen ödemeyi yeniden başlatın.');
      } else if (['intent_created', 'receipt_uploaded', 'manual_review'].includes(paymentState)) {
        setInviteSaveError('Ödeme incelemede. Onaylanınca davetiye otomatik yayınlanacak.');
      }
      return false;
    }

    publishAttemptInFlightRef.current = true;
    try {
      await publishInvitation();
      return true;
    } finally {
      publishAttemptInFlightRef.current = false;
    }
  }, [pendingInvitePublish, eventId, refreshInvitationStatus, publishInvitation]);

  useEffect(() => {
    if (!pendingInvitePublish || !eventId) return undefined;
    let cancelled = false;

    const checkAndPublish = async () => {
      if (cancelled) return;
      await tryAutoPublishAfterPayment();
    };

    checkAndPublish();
    const timer = setInterval(checkAndPublish, 15000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [pendingInvitePublish, eventId, tryAutoPublishAfterPayment]);

  const loadResponseSummary = useCallback(async () => {
    if (!eventId) return;
    setResponseSummaryLoading(true);
    setResponseSummaryError('');
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.get(`${API_BASE_URL}/api/special-days/events/${eventId}/response-summary`, { headers });
      setResponseAggregate(data?.aggregate || null);
      setInviteResponses(Array.isArray(data?.responses) ? data.responses : []);
      if (data?.survey?.closeAt) setSurveyCloseAt(data.survey.closeAt);
      if (Number.isFinite(Number(data?.survey?.activeDays))) setSurveyActiveDays(Number(data.survey.activeDays));
      setShowResponseSummary(true);
    } catch (error) {
      console.error('Special days response summary error:', error);
      setResponseSummaryError('Anket sonuçları şu an alınamadı. Lütfen tekrar deneyin.');
    } finally {
      setResponseSummaryLoading(false);
    }
  }, [eventId]);

  const handleSurveyFlowComplete = useCallback(async () => {
    setSurveyLinkModalOpen(false);
    await loadResponseSummary();
  }, [loadResponseSummary]);

  const runPreferenceEngine = useCallback(async (guestCountOverride) => {
    if (!eventId) return;
    const guestCount = Math.max(1, Number(guestCountOverride || setup.guestCount || 0));
    const signature = `${eventId}:${guestCount}`;
    if (preferenceLoadSignatureRef.current === signature) return;
    preferenceLoadSignatureRef.current = signature;

    setPreferenceLoading(true);
    setPreferenceError('');
    try {
      const headers = getAuthHeaders();
      const { data } = await axios.post(
        `${API_BASE_URL}/api/special-days/setup-preview`,
        {
          eventId,
          eventType: setup.eventType,
          guestCount,
          serviceMode: setup.serviceMode,
          isMealHosted: !!setup.isMealHosted,
          serviceStyle: setup.serviceStyle,
          dishTypeFilters: buildDishTypeFiltersBySetup({
            serviceMode: setup.serviceMode,
            isMealHosted: !!setup.isMealHosted,
            serviceStyle: setup.serviceStyle,
            dishTypeFilters: setup.dishTypeFilters
          }),
          mainCourseFamily: setup.mainCourseFamily,
          healthCheckEnabled: !!setup.healthCheckEnabled,
          phone: String(setup.phone || '').trim(),
          city: String(setup.city || '').trim(),
          district: String(setup.district || '').trim()
        },
        { headers }
      );
      setRecipes(Array.isArray(data?.recipes) ? data.recipes : []);
      setSetup((prev) => ({ ...prev, guestCount }));
    } catch (error) {
      console.error('Special days preference engine error:', error);
      setPreferenceError('Tercih motoru sonuçları şu an alınamadı. Lütfen Yenile ile tekrar deneyin.');
    } finally {
      setPreferenceLoading(false);
    }
  }, [
    eventId,
    setup.eventType,
    setup.guestCount,
    setup.serviceMode,
    setup.isMealHosted,
    setup.serviceStyle,
    setup.mainCourseFamily,
    setup.healthCheckEnabled,
    setup.phone,
    setup.city,
    setup.district
  ]);

  const openPreferenceLibrary = useCallback(async () => {
    const selectedFilters = buildDishTypeFiltersBySetup({
      serviceMode: setup.serviceMode,
      isMealHosted: !!setup.isMealHosted,
      serviceStyle: setup.serviceStyle,
      dishTypeFilters: setup.dishTypeFilters
    });

    setPreferenceFilters({
      sweet: selectedFilters.includes('sweet'),
      savory: selectedFilters.includes('savory'),
      meze: selectedFilters.includes('meze')
    });
    setPreferenceLibraryOpen(true);
    setRecipeSearchTerm('');
    if (!recipes.length) {
      await runPreferenceEngine(Number(responseAggregate?.confirmedGuestCount || setup.guestCount || 0));
    }
  }, [
    setup.serviceMode,
    setup.isMealHosted,
    setup.serviceStyle,
    setup.dishTypeFilters,
    setup.guestCount,
    recipes.length,
    responseAggregate?.confirmedGuestCount,
    runPreferenceEngine
  ]);

  const togglePreferenceFilter = useCallback((key) => {
    setPreferenceFilters((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  useEffect(() => {
    preferenceLoadSignatureRef.current = '';
  }, [eventId]);

  useEffect(() => {
    if (!surveyCloseAt || !showResponseSummary) {
      setSurveyRemainingMs(null);
      return undefined;
    }

    const closeAtMs = new Date(surveyCloseAt).getTime();
    if (!Number.isFinite(closeAtMs)) {
      setSurveyRemainingMs(null);
      return undefined;
    }

    const update = () => {
      const remaining = Math.max(0, closeAtMs - Date.now());
      setSurveyRemainingMs(remaining);
    };

    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [surveyCloseAt, showResponseSummary]);

  useEffect(() => {
    if (!showResponseSummary) return;
    if (surveyRemainingMs === null || surveyRemainingMs > 0) return;
    const confirmedGuestCount = Number(responseAggregate?.confirmedGuestCount || 0);
    if (!Number.isFinite(confirmedGuestCount) || confirmedGuestCount <= 0) return;
    runPreferenceEngine(confirmedGuestCount);
  }, [showResponseSummary, surveyRemainingMs, responseAggregate?.confirmedGuestCount, runPreferenceEngine]);

  const handleFinalizeInventory = async (action) => {
    if (!eventId || !eventInventory.length) return;
    setLeftoverProcessing(true);
    try {
      const headers = getAuthHeaders();
      const actions = eventInventory.flatMap((item) =>
        (item.batchIds || []).map((batchId) => ({
          batchId,
          action
        }))
      );

      const { data } = await axios.post(
        `${API_BASE_URL}/api/special-days/events/${eventId}/finalize-leftovers`,
        { actions },
        { headers }
      );

      setEventInventory(Array.isArray(data?.inventory) ? data.inventory : []);
    } catch (error) {
      console.error('Special days finalize leftovers error:', error);
    } finally {
      setLeftoverProcessing(false);
    }
  };

  const setupGateForm = (
    <form className="grid gap-4 xl:grid-cols-3" onSubmit={handleSetupSubmit}>
      <div className="space-y-1.5 xl:col-span-3">
        <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Etkinlik tipi</label>
        <div className="grid gap-3 sm:grid-cols-2">
          {EVENT_TYPES.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => handleChange('eventType', item.value)}
              className={`rounded-[1rem] border px-4 py-3 text-left transition ${setup.eventType === item.value
                  ? 'border-rose-300 bg-rose-50 dark:border-rose-900/40 dark:bg-rose-950/10'
                  : 'border-[#ece7e7] bg-white hover:border-rose-200 dark:border-gray-800 dark:bg-[#0b0b0b]'
                }`}
            >
              <p className="text-[15px] font-black text-slate-900 dark:text-white">{item.label}</p>
              <p className="mt-0.5 text-[12px] leading-5 font-medium text-slate-500 dark:text-slate-400">{item.description}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="space-y-1.5 xl:col-span-3">
        <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Misafire uygun tarif filtreleri</label>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {GUEST_DISH_FILTER_OPTIONS.map((option) => {
            const active = buildDishTypeFiltersBySetup({
              serviceMode: setup.serviceMode,
              isMealHosted: !!setup.isMealHosted,
              serviceStyle: setup.serviceStyle,
              dishTypeFilters: setup.dishTypeFilters
            }).includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleChange('dishTypeFilters', option.value)}
                className={`rounded-[1rem] border px-4 py-2.5 text-left text-[15px] font-black transition ${active
                    ? 'border-rose-300 bg-rose-50 text-rose-600 dark:border-rose-900/40 dark:bg-rose-950/10 dark:text-rose-300'
                    : 'border-[#ece7e7] bg-white text-slate-700 hover:border-rose-200 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-slate-200'
                  }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-1.5 xl:col-span-3">
        <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Davet akışı nasıl olacak?</label>
        <div className="grid gap-3 sm:grid-cols-3">
          {[
            { value: 'meal', label: 'Yemekli olacak' },
            { value: 'snack', label: 'İkramlık olacak' },
            { value: 'both', label: 'Her ikisi de' }
          ].map((option) => (
            <button
              key={String(option.value)}
              type="button"
              onClick={() => handleChange('serviceMode', option.value)}
              className={`rounded-[1rem] border px-4 py-2.5 text-left text-[15px] font-black transition ${setup.serviceMode === option.value
                  ? 'border-rose-300 bg-rose-50 text-rose-600 dark:border-rose-900/40 dark:bg-rose-950/10 dark:text-rose-300'
                  : 'border-[#ece7e7] bg-white text-slate-700 hover:border-rose-200 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-slate-200'
                }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {setup.serviceMode !== 'meal' && (
        <div className="space-y-1.5 xl:col-span-3">
          <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Servis stili</label>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {SERVICE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleChange('serviceStyle', option.value)}
                className={`rounded-[1rem] border px-4 py-2.5 text-left text-[15px] font-black transition ${setup.serviceStyle === option.value
                    ? 'border-rose-300 bg-rose-50 text-rose-600 dark:border-rose-900/40 dark:bg-rose-950/10 dark:text-rose-300'
                    : 'border-[#ece7e7] bg-white text-slate-700 hover:border-rose-200 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-slate-200'
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {setup.serviceMode !== 'snack' && (
        <div className="space-y-1.5 xl:col-span-3">
          <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Ana yemek ailesi</label>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {MAIN_COURSE_FAMILIES.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleChange('mainCourseFamily', option.value)}
                className={`rounded-[1rem] border px-4 py-2.5 text-left text-[15px] font-black transition ${setup.mainCourseFamily === option.value
                    ? 'border-rose-300 bg-rose-50 text-rose-600 dark:border-rose-900/40 dark:bg-rose-950/10 dark:text-rose-300'
                    : 'border-[#ece7e7] bg-white text-slate-700 hover:border-rose-200 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-slate-200'
                  }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <label className="flex items-center gap-3 rounded-[1rem] border border-[#ece7e7] px-4 py-2.5 text-[15px] font-bold text-slate-700 xl:col-span-3 dark:border-gray-800 dark:text-slate-200">
        <input
          type="checkbox"
          checked={setup.healthCheckEnabled}
          onChange={(e) => handleChange('healthCheckEnabled', e.target.checked)}
          className="size-4 rounded border-slate-300 text-rose-500 focus:ring-rose-500"
        />
        Davetiyede sağlık kontrolü olsun mu?
      </label>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 xl:col-span-3">
        <div className="space-y-1.5">
          <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Telefon</label>
          <input
            type="tel"
            value={setup.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="05xx xxx xx xx"
            className="w-full rounded-[1rem] border border-[#ece7e7] bg-white px-4 py-2.5 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">İl</label>
          <select
            value={setup.city}
            onChange={(e) => handleChange('city', e.target.value)}
            className="w-full rounded-[1rem] border border-[#ece7e7] bg-white px-4 py-2.5 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
          >
            <option value="">İl seçin</option>
            {cityOptions.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-1.5 xl:col-span-3">
        <label className="text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">İlçe</label>
        <select
          value={setup.district}
          onChange={(e) => handleChange('district', e.target.value)}
          disabled={!setup.city}
          className="w-full rounded-[1rem] border border-[#ece7e7] bg-white px-4 py-2.5 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
        >
          <option value="">{setup.city ? 'İlçe seçin' : 'Önce il seçin'}</option>
          {setupDistrictOptions.map((district) => (
            <option key={district} value={district}>
              {district}
            </option>
          ))}
        </select>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full rounded-[1rem] bg-rose-500 px-5 py-3 text-sm font-black uppercase tracking-[0.12em] text-white transition xl:col-span-3 hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-70"
      >
        {loading ? 'Menü hazırlanıyor...' : 'İçeriği Gör'}
      </button>
    </form>
  );

  return (
    <DashboardLayout isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode}>
      <div className="relative">
        <div className={`space-y-8 ${!setupCompleted ? 'pointer-events-none select-none blur-sm' : ''}`}>
          {invitePublished && !showResponseSummary && (
            <>
              <section className="overflow-hidden rounded-[2rem] border border-[#ece7e7] bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-[#050505]">
                <div className="max-w-3xl space-y-3">
                  <div className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-4 py-2 text-[11px] font-black uppercase tracking-[0.18em] text-rose-500 dark:border-rose-900/30 dark:bg-rose-950/10 dark:text-rose-400">
                    <span className="material-symbols-outlined text-base">celebration</span>
                    Özel Günler Mutfağı
                  </div>
                  <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">
                    Benim için bir organizasyon planla
                  </h1>
                  <p className="max-w-2xl text-sm font-medium text-slate-500 dark:text-slate-400">
                    Menüye ulaşmadan önce kısa etkinlik bilgilerini alıyoruz. Böylece doğum günü veya altın günü için doğru
                    tarif havuzunu, eksik malzeme listesini ve sonraki davetiye adımlarını daha temiz kurabiliyoruz.
                  </p>
                </div>
              </section>

              <section className="grid gap-8 xl:grid-cols-[0.95fr_1.25fr]">
                <div className="rounded-[2rem] border border-[#ece7e7] bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-[#050505]">
                  <div className="mb-5">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Etkinlik Girişi</p>
                    <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                      Menüye erişmek için bilgileri doldurun
                    </h2>
                  </div>

                  <form className="space-y-5" onSubmit={handleSetupSubmit}>
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Etkinlik tipi</label>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {EVENT_TYPES.map((item) => (
                          <button
                            key={item.value}
                            type="button"
                            onClick={() => handleChange('eventType', item.value)}
                            className={`rounded-[1.2rem] border px-4 py-4 text-left transition ${setup.eventType === item.value
                                ? 'border-rose-300 bg-rose-50 dark:border-rose-900/40 dark:bg-rose-950/10'
                                : 'border-[#ece7e7] bg-white hover:border-rose-200 dark:border-gray-800 dark:bg-[#0b0b0b]'
                              }`}
                          >
                            <p className="text-sm font-black text-slate-900 dark:text-white">{item.label}</p>
                            <p className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">{item.description}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Misafire uygun tarif filtreleri</label>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {GUEST_DISH_FILTER_OPTIONS.map((option) => {
                          const active = buildDishTypeFiltersBySetup({
                            serviceMode: setup.serviceMode,
                            isMealHosted: !!setup.isMealHosted,
                            serviceStyle: setup.serviceStyle,
                            dishTypeFilters: setup.dishTypeFilters
                          }).includes(option.value);
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleChange('dishTypeFilters', option.value)}
                              className={`rounded-[1rem] border px-4 py-3 text-left text-sm font-black transition ${active
                                  ? 'border-rose-300 bg-rose-50 text-rose-600 dark:border-rose-900/40 dark:bg-rose-950/10 dark:text-rose-300'
                                  : 'border-[#ece7e7] bg-white text-slate-700 hover:border-rose-200 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-slate-200'
                                }`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-1">
                      <div className="space-y-2">
                        <label className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Servis şekli</label>
                        <select
                          value={setup.serviceStyle}
                          onChange={(e) => handleChange('serviceStyle', e.target.value)}
                          className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                        >
                          {SERVICE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <label className="flex items-center gap-3 rounded-2xl border border-[#ece7e7] px-4 py-3 text-sm font-bold text-slate-700 dark:border-gray-800 dark:text-slate-200">
                      <input
                        type="checkbox"
                        checked={setup.healthCheckEnabled}
                        onChange={(e) => handleChange('healthCheckEnabled', e.target.checked)}
                        className="size-4 rounded border-slate-300 text-rose-500 focus:ring-rose-500"
                      />
                      Davetiyede sağlık kontrolü olsun mu?
                    </label>

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <label className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Telefon</label>
                        <input
                          type="tel"
                          value={setup.phone}
                          onChange={(e) => handleChange('phone', e.target.value)}
                          placeholder="05xx xxx xx xx"
                          className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">İl</label>
                        <input
                          type="text"
                          value={setup.city}
                          onChange={(e) => handleChange('city', e.target.value)}
                          placeholder="İstanbul"
                          className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">İlçe</label>
                      <input
                        type="text"
                        value={setup.district}
                        onChange={(e) => handleChange('district', e.target.value)}
                        placeholder="Kadıköy"
                        className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full rounded-[1.2rem] bg-rose-500 px-5 py-3.5 text-sm font-black uppercase tracking-[0.14em] text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      {loading ? 'Menü hazırlanıyor...' : 'İçeriği Gör'}
                    </button>
                  </form>
                </div>

                <div className="rounded-[2rem] border border-[#ece7e7] bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-[#050505]">
                  <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Uygun Tarif Havuzu</p>
                      <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                        {activeEvent.label} için önerilen tarifler
                      </h2>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {setupCompleted && (
                        <div className="rounded-full bg-slate-100 px-3 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-500 dark:bg-white/5 dark:text-slate-300">
                          {recipes.length} tarif bulundu
                        </div>
                      )}
                      {setupCompleted && (
                        <button
                          type="button"
                          onClick={handleShoppingPreview}
                          disabled={!selectedRecipeIds.length || shoppingLoading}
                          className="rounded-full bg-rose-500 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {shoppingLoading ? 'Listeleniyor...' : `Eksikleri Listele (${selectedRecipeIds.length})`}
                        </button>
                      )}
                    </div>
                  </div>

                  {!setupCompleted ? (
                    <div className="flex min-h-[420px] items-center justify-center rounded-[1.5rem] border border-dashed border-[#ece7e7] bg-slate-50/70 px-6 text-center dark:border-gray-800 dark:bg-[#0b0b0b]">
                      <div className="max-w-md space-y-3">
                        <span className="material-symbols-outlined text-4xl text-rose-400">event_available</span>
                        <p className="text-lg font-black text-slate-900 dark:text-white">Kurulumu tamamlayınca menü açılacak</p>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          Kişi sayısı, servis dengesi ve ilçe bilgisi geldiğinde bu özel gün için işaretlenmiş tarifleri
                          getireceğiz. Bir sonraki adımda bunlardan seçim yapıp eksik malzemeleri listeleyeceğiz.
                        </p>
                      </div>
                    </div>
                  ) : recipes.length === 0 ? (
                    <div className="flex min-h-[420px] items-center justify-center rounded-[1.5rem] border border-dashed border-[#ece7e7] bg-slate-50/70 px-6 text-center dark:border-gray-800 dark:bg-[#0b0b0b]">
                      <div className="max-w-md space-y-3">
                        <span className="material-symbols-outlined text-4xl text-amber-400">inventory_2</span>
                        <p className="text-lg font-black text-slate-900 dark:text-white">Henüz uygun tarif bulunamadı</p>
                        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
                          Admin tarafında bu etkinlik için işaretlenmiş tarif sayısı arttıkça bu alan otomatik büyüyecek.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3">
                      {recipes.map((recipe) => (
                        <RecipeCard
                          key={recipe.id || recipe.name}
                          recipe={recipe}
                          onCardClick={handleRecipeClick}
                          onToggleSelect={toggleRecipeSelection}
                          isSelected={selectedRecipeIds.includes(String(recipe.id))}
                          isDarkMode={isDarkMode}
                          userPortion={Number(setup.guestCount || 4)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </section>
            </>
          )}

          {invitePublished && !showResponseSummary && shoppingPreview && (
            <section className="rounded-[2rem] border border-[#ece7e7] bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-[#050505]">
              <div className="mb-5 flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Alışveriş Ön İzlemesi</p>
                  <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                    Eksik malzeme listesi
                  </h2>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="rounded-full bg-slate-100 px-3 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-500 dark:bg-white/5 dark:text-slate-300">
                    {shoppingPreview.selectedCount || 0} tarif seçildi
                  </div>
                  {!!shoppingPreview.missingItems?.length && (
                    <button
                      type="button"
                      onClick={handleOpenShoppingScheduleModal}
                      className="rounded-full bg-sky-500 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-sky-600"
                    >
                      Alışveriş Listesine Gönder
                    </button>
                  )}
                  {!!shoppingPreview.missingItems?.length && (
                    <button
                      type="button"
                      onClick={handleAddMissingToEventInventory}
                      disabled={inventoryLoading}
                      className="rounded-full bg-emerald-500 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {inventoryLoading ? 'Ekleniyor...' : 'Aldım, Event Stoğuna Ekle'}
                    </button>
                  )}
                </div>
              </div>

              {!shoppingPreview.missingItems?.length ? (
                <div className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50 px-5 py-5 text-sm font-bold text-emerald-700 dark:border-emerald-900/30 dark:bg-emerald-950/10 dark:text-emerald-300">
                  Seçtiğiniz tarifler için şu an ek eksik malzeme görünmüyor. Bir sonraki adımda bunu event alışveriş listesine çevirebiliriz.
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {shoppingPreview.missingItems.map((item) => (
                    <div
                      key={`${item.name}-${item.unit}`}
                      className="rounded-[1.4rem] border border-[#ece7e7] bg-slate-50/70 p-4 dark:border-gray-800 dark:bg-[#0b0b0b]"
                    >
                      <p className="text-sm font-black text-slate-900 dark:text-white">{item.name}</p>
                      <p className="mt-2 text-xs font-bold text-rose-500">
                        {Number(item.quantity || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} {item.unit || ''}
                      </p>
                      <p className="mt-2 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                        Tarifler: {Array.isArray(item.recipes) ? item.recipes.join(', ') : '-'}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {invitePublished && !showResponseSummary && (setupCompleted || eventInventory.length > 0) && (
            <section className="rounded-[2rem] border border-[#ece7e7] bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-[#050505]">
              <div className="mb-5 flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Sanal Stok</p>
                  <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                    Event stoğu
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => refreshEventInventory()}
                  disabled={!eventId || inventoryLoading}
                  className="rounded-full bg-slate-100 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-500 transition hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {inventoryLoading ? 'Yükleniyor...' : 'Stoğu Yenile'}
                </button>
              </div>

              {!eventInventory.length ? (
                <div className="rounded-[1.5rem] border border-dashed border-[#ece7e7] bg-slate-50/70 px-5 py-6 text-sm font-medium text-slate-500 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-slate-400">
                  Event stoğu henüz boş. Eksik malzeme listesinden `Aldım` akışını çalıştırınca buraya düşecek.
                </div>
              ) : (
                <>
                  <div className="mb-5 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleFinalizeInventory('transfer_home')}
                      disabled={leftoverProcessing}
                      className="rounded-full bg-emerald-500 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {leftoverProcessing ? 'İşleniyor...' : 'Kalanları Eve Aktar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleFinalizeInventory('consumed')}
                      disabled={leftoverProcessing}
                      className="rounded-full bg-slate-700 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Tamamı Tüketildi
                    </button>
                    <button
                      type="button"
                      onClick={() => handleFinalizeInventory('discarded')}
                      disabled={leftoverProcessing}
                      className="rounded-full bg-amber-500 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Atıldı Olarak Kapat
                    </button>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {eventInventory.map((item) => (
                      <div
                        key={`${item.dictionaryId || item.ingredientName}-${item.unit}`}
                        className="rounded-[1.4rem] border border-[#ece7e7] bg-slate-50/70 p-4 dark:border-gray-800 dark:bg-[#0b0b0b]"
                      >
                        <p className="text-sm font-black text-slate-900 dark:text-white">{item.ingredientName}</p>
                        <p className="mt-2 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                          {Number(item.totalRemaining || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} {item.unit}
                        </p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>
          )}

          {setupCompleted && !showResponseSummary && (
            <section className="rounded-[2rem] border border-[#ece7e7] bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-[#050505]">
              <div className="mb-5">
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Davetiye</p>
                <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                  Parti nerede olacak?
                </h2>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Tema seçimi</label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {inviteTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handleInviteChange('templateId', template.id)}
                        className={`rounded-[1.2rem] border px-4 py-4 text-left transition ${inviteForm.templateId === template.id
                            ? 'border-rose-300 bg-rose-50 dark:border-rose-900/40 dark:bg-rose-950/10'
                            : 'border-[#ece7e7] bg-white hover:border-rose-200 dark:border-gray-800 dark:bg-[#0b0b0b]'
                          }`}
                      >
                        <p className="text-sm font-black text-slate-900 dark:text-white">{template.label}</p>
                        <p className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">{template.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <input
                    type="text"
                    value={inviteForm.hostName}
                    onChange={(e) => handleInviteChange('hostName', e.target.value)}
                    placeholder="Ev sahibi adı"
                    className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                  />
                  <input
                    type="text"
                    value={inviteForm.eventTitle}
                    onChange={(e) => handleInviteChange('eventTitle', e.target.value)}
                    placeholder="Davet başlığı"
                    className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <input
                    type="date"
                    value={inviteForm.eventDate}
                    onChange={(e) => handleInviteChange('eventDate', e.target.value)}
                    className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                  />
                  <input
                    type="time"
                    value={inviteForm.eventTime}
                    onChange={(e) => handleInviteChange('eventTime', e.target.value)}
                    className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                  />
                </div>

                <label className="flex items-center gap-3 rounded-2xl border border-[#ece7e7] px-4 py-3 text-sm font-bold text-slate-700 dark:border-gray-800 dark:text-slate-200">
                  <input
                    type="checkbox"
                    checked={inviteForm.useExistingAddress}
                    onChange={(e) => handleInviteChange('useExistingAddress', e.target.checked)}
                    className="size-4 rounded border-slate-300 text-rose-500 focus:ring-rose-500"
                  />
                  Mevcut adresi kullan
                </label>

                <div className="grid gap-4 sm:grid-cols-2">
                  <input
                    type="text"
                    value={inviteForm.addressTitle}
                    onChange={(e) => handleInviteChange('addressTitle', e.target.value)}
                    placeholder="Adres Başlığı"
                    className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                  />
                  <input
                    type="text"
                    value={inviteForm.fullAddress}
                    onChange={(e) => handleInviteChange('fullAddress', e.target.value)}
                    placeholder="Parti adresi"
                    className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <select
                    value={inviteForm.city}
                    onChange={(e) => handleInviteChange('city', e.target.value)}
                    className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                  >
                    <option value="">İl seçin</option>
                    {cityOptions.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                  <select
                    value={inviteForm.district}
                    onChange={(e) => handleInviteChange('district', e.target.value)}
                    disabled={!inviteForm.city}
                    className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                  >
                    <option value="">{inviteForm.city ? 'İlçe seçin' : 'Önce il seçin'}</option>
                    {inviteDistrictOptions.map((district) => (
                      <option key={district} value={district}>
                        {district}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rounded-[1.4rem] border border-[#ece7e7] bg-slate-50/70 p-4 dark:border-gray-800 dark:bg-[#0b0b0b]">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Konum</p>

                  {mapError && (
                    <div className="mt-2 rounded-xl bg-rose-50 p-3 text-xs font-bold text-rose-600 dark:bg-rose-950/20 dark:text-rose-400">
                      {mapError}
                    </div>
                  )}

                  <div className="relative mt-4 overflow-hidden rounded-[1.2rem] border border-[#ece7e7] bg-white dark:border-gray-800 dark:bg-[#101010]">
                    <div
                      ref={mapDivRef}
                      className="h-[320px] w-full"
                      style={{ background: isDarkMode ? '#151515' : '#f8f9fa' }}
                    />
                    {!isMapReady && !lat && !lng && (
                      <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-6 text-center">
                        <div className="max-w-[240px] space-y-2">
                          <span className="material-symbols-outlined text-4xl text-slate-300">map</span>
                          <p className="text-xs font-medium text-slate-500">Özel pini istediğin yere sürükle veya haritada istediğin yere tıkla.</p>
                        </div>
                      </div>
                    )}
                    {formattedAddress && (
                      <div className="border-t border-[#ece7e7] bg-white/50 px-4 py-2 text-[11px] font-semibold text-slate-500 dark:border-gray-800 dark:text-slate-400">
                        Bulunan Adres: {formattedAddress}
                      </div>
                    )}
                    {locationConfirmed && (
                      <div className="flex items-center gap-2 border-t border-emerald-100 bg-emerald-50/50 px-4 py-2 text-[11px] font-black uppercase tracking-wider text-emerald-600 dark:border-emerald-900/20 dark:bg-emerald-950/10">
                        <span className="material-symbols-outlined text-sm">check_circle</span>
                        Konum Doğrulandı
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handlePinAction}
                      disabled={isGeocoding}
                      className="flex items-center gap-2 rounded-full bg-rose-500 px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-rose-600 disabled:opacity-60"
                    >
                      <span className="material-symbols-outlined text-sm">pin_drop</span>
                      {isGeocoding ? 'Çözülüyor...' : 'Pin Koy'}
                    </button>

                    {lat && lng && (
                      <>
                        <button
                          type="button"
                          onClick={handleConfirmLocation}
                          className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.14em] transition ${locationConfirmed
                              ? 'bg-emerald-500 text-white'
                              : 'bg-sky-500 text-white hover:bg-sky-600'
                            }`}
                        >
                          <span className="material-symbols-outlined text-sm">verified</span>
                          {locationConfirmed ? 'Konum Doğru' : 'Konumu Onayla'}
                        </button>
                        <button
                          type="button"
                          onClick={handleResetLocation}
                          className="flex items-center gap-2 rounded-full bg-slate-200 px-4 py-2.5 text-[11px] font-black uppercase tracking-[0.14em] text-slate-600 transition hover:bg-slate-300 dark:bg-white/10 dark:text-slate-300"
                        >
                          <span className="material-symbols-outlined text-sm">delete_sweep</span>
                          Konumu Temizle
                        </button>
                      </>
                    )}
                  </div>

                  {!locationConfirmed && lat && (
                    <p className="mt-3 flex items-center gap-1.5 text-[11px] font-bold text-amber-500">
                      <span className="material-symbols-outlined text-sm">info</span>
                      Önce pin koy de, sonra haritada binanın üstüne tıkla veya pini sürükle.
                    </p>
                  )}
                </div>

                <textarea
                  value={inviteForm.note}
                  onChange={(e) => handleInviteChange('note', e.target.value)}
                  placeholder="Kısa davet notu"
                  rows={3}
                  className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                />

                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">
                    Anket Linki Kaç Gün Aktif Kalsın?
                  </label>
                  <select
                    value={surveyActiveDays}
                    onChange={(e) => setSurveyActiveDays(Number(e.target.value) || 3)}
                    className="w-full rounded-2xl border border-[#ece7e7] bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none transition focus:border-rose-300 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-white"
                  >
                    {[1, 2, 3, 5, 7, 10, 14, 21, 30].map((day) => (
                      <option key={day} value={day}>
                        {day} gün
                      </option>
                    ))}
                  </select>
                </div>

                {locationConfirmed ? (
                  <p className="text-xs font-bold text-slate-500 dark:text-slate-400">
                    Adres seçildi. Önizleme otomatik hazırlanıyor.
                  </p>
                ) : (
                  <p className="text-xs font-bold text-amber-500">
                    Önizleme için önce haritada konumu seçip onaylayın.
                  </p>
                )}

                {invitePreviewHtml && (
                  <div className="overflow-hidden rounded-[1.8rem] border border-[#ece7e7] bg-slate-50/70 p-3 dark:border-gray-800 dark:bg-[#0b0b0b]">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-slate-500">Davetiye önizleme</p>
                      <span className="text-[11px] font-bold text-slate-400">Seçilen tema: {inviteForm.templateId}</span>
                    </div>
                    <iframe
                      title="Davet Önizleme"
                      srcDoc={invitePreviewHtml}
                      className="h-[640px] w-full rounded-[1.2rem] border border-[#ece7e7] bg-white dark:border-gray-800"
                    />
                  </div>
                )}

                {inviteSaveError && (
                  <p className="text-xs font-bold text-rose-500">{inviteSaveError}</p>
                )}

                {invitePublishStatus && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-600 dark:border-slate-800 dark:bg-[#0b0b0b] dark:text-slate-300">
                    Durum: {String(invitePublishStatus?.invitationState || 'invitation_draft')} | Ödeme: {String(invitePublishStatus?.payment?.state || 'none')} | Erişim: {String(invitePublishStatus?.access?.state || 'inactive')}
                  </div>
                )}

                <div className="pt-2 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={saveInviteDraft}
                    disabled={!eventId || inviteSaving || !inviteForm.templateId}
                    className="rounded-full border border-slate-300 bg-white px-5 py-3 text-[11px] font-black uppercase tracking-[0.14em] text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-[#0b0b0b] dark:text-slate-200 dark:hover:bg-slate-900"
                  >
                    {inviteSaving ? 'Kaydediliyor...' : 'Taslağı Kaydet'}
                  </button>
                  <button
                    type="button"
                    onClick={publishInvitation}
                    disabled={!eventId || inviteSaving || !inviteForm.templateId || !locationConfirmed || invitePreviewLoading || !invitePreviewHtml || inviteStatusLoading}
                    className="rounded-full bg-rose-500 px-5 py-3 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {inviteSaving ? 'Yayınlanıyor...' : 'Yayınla'}
                  </button>
                </div>
              </div>
            </section>
          )}

          {showResponseSummary && (
            <section className="rounded-[2rem] border border-[#ece7e7] bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-[#050505]">
              <div className="mb-5 flex items-end justify-between gap-4">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Anket Sonuçları</p>
                  <h2 className="mt-2 text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                    Katılım Listesi
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={loadResponseSummary}
                  disabled={responseSummaryLoading}
                  className="rounded-full bg-slate-100 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-500 transition hover:bg-slate-200 dark:bg-white/5 dark:text-slate-300 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {responseSummaryLoading ? 'Yükleniyor...' : 'Yenile'}
                </button>
              </div>

              {surveyRemainingMs !== null && (
                <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 dark:border-sky-900/40 dark:bg-sky-950/20">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-sky-700 dark:text-sky-300">
                    Anket Sayaç
                  </p>
                  {surveyRemainingMs > 0 ? (
                    <p className="mt-1 text-sm font-bold text-sky-700 dark:text-sky-300">
                      Kalan süre: {Math.floor(surveyRemainingMs / (1000 * 60 * 60 * 24))} gün{' '}
                      {Math.floor((surveyRemainingMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))} saat{' '}
                      {Math.floor((surveyRemainingMs % (1000 * 60 * 60)) / (1000 * 60))} dakika
                    </p>
                  ) : (
                    <p className="mt-1 text-sm font-bold text-emerald-700 dark:text-emerald-300">
                      Anket süresi doldu. Sonuçlar tercih motoruna aktarılıyor.
                    </p>
                  )}
                </div>
              )}

              {responseSummaryError && (
                <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-bold text-rose-600 dark:border-rose-900/40 dark:bg-rose-950/20 dark:text-rose-300">
                  {responseSummaryError}
                </div>
              )}

              {responseAggregate && (
                <div className="mb-4 grid gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-[#0b0b0b]">
                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Toplam Yanıt</p>
                    <p className="mt-1 text-lg font-black text-slate-900 dark:text-white">{Number(responseAggregate.responseCount || 0)}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-[#0b0b0b]">
                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Gelecek Kişi</p>
                    <p className="mt-1 text-lg font-black text-slate-900 dark:text-white">{Number(responseAggregate.confirmedGuestCount || 0)}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-[#0b0b0b]">
                    <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Bebek Katılımı</p>
                    <p className="mt-1 text-lg font-black text-slate-900 dark:text-white">{responseAggregate.hasBabyGuests ? 'Var' : 'Yok'}</p>
                  </div>
                </div>
              )}

              {!inviteResponses.length ? (
                <div className="rounded-[1.5rem] border border-dashed border-[#ece7e7] bg-slate-50/70 px-5 py-6 text-sm font-medium text-slate-500 dark:border-gray-800 dark:bg-[#0b0b0b] dark:text-slate-400">
                  Henüz katılım yanıtı gelmedi.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                  <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                    <thead className="bg-slate-50 dark:bg-slate-900/60">
                      <tr>
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Kişi Adı</th>
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Kaç Kişi</th>
                        {shouldShowHealthColumn && (
                          <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Sağlık Durumu</th>
                        )}
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Bebek Var mı?</th>
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-[0.14em] text-slate-500">Katılım Notu</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                      {inviteResponses.map((row) => (
                        <tr key={row.id} className="bg-white dark:bg-[#050505]">
                          <td className="px-4 py-3 text-sm font-bold text-slate-900 dark:text-white">{row.fullName || '-'}</td>
                          <td className="px-4 py-3 text-sm font-bold text-slate-700 dark:text-slate-200">{Number(row.attendeeCount || 0)}</td>
                          {shouldShowHealthColumn && (
                            <td className="px-4 py-3 text-sm font-medium text-slate-600 dark:text-slate-300">
                              {formatHealthFlags(row.healthFlags)}
                            </td>
                          )}
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 dark:text-slate-300">{row.hasBabyGuest ? 'Evet' : 'Hayır'}</td>
                          <td className="px-4 py-3 text-sm font-medium text-slate-600 dark:text-slate-300">{row.note || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {surveyRemainingMs !== null && surveyRemainingMs <= 0 && (
                <div className="mt-6 rounded-[1.5rem] border border-[#ece7e7] bg-slate-50/70 p-4 dark:border-gray-800 dark:bg-[#0b0b0b]">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Tercih Motoru</p>
                      <h3 className="mt-1 text-lg font-black tracking-tight text-slate-900 dark:text-white">
                        Anket sonuçlarına göre önerilen menü
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={openPreferenceLibrary}
                      disabled={preferenceLoading}
                      className="rounded-full bg-rose-500 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {preferenceLoading ? 'Hazırlanıyor...' : 'Tercihlere Göre Listele'}
                    </button>
                  </div>

                  {preferenceError && (
                    <p className="mb-3 text-xs font-bold text-rose-500">{preferenceError}</p>
                  )}

                  {preferenceLoading ? (
                    <div className="rounded-xl border border-dashed border-[#ece7e7] bg-white/70 px-4 py-5 text-sm font-medium text-slate-500 dark:border-gray-800 dark:bg-[#050505] dark:text-slate-400">
                      Tercih motoru çalışıyor, menü hazırlanıyor...
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-[#ece7e7] bg-white/70 px-4 py-5 text-sm font-medium text-slate-500 dark:border-gray-800 dark:bg-[#050505] dark:text-slate-400">
                      Tarifler hazır. "Tercihlere Göre Listele" ile kütüphane görünümüne geçebilirsiniz.
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {showResponseSummary && preferenceLibraryOpen && surveyRemainingMs !== null && surveyRemainingMs <= 0 && (
            <section className="rounded-[2rem] border border-[#ece7e7] bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-[#050505]">
              <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-[11px] font-black uppercase tracking-[0.16em] text-slate-400">Tarif Kütüphanesi</p>
                  <h3 className="mt-1 text-xl font-black tracking-tight text-slate-900 dark:text-white">
                    Tercihlere göre listeleme
                  </h3>
                  <p className="mt-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                    İlk form seçimlerine göre filtreler aktif geldi. Tarif seçip Alakart benzeri malzeme özetini çıkarabilirsiniz.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPreferenceLibraryOpen(false)}
                  className="rounded-full border border-slate-300 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Kapat
                </button>
              </div>

              <div className="mb-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                    <span className="material-symbols-outlined text-lg">search</span>
                  </span>
                  <input
                    type="text"
                    value={recipeSearchTerm}
                    onChange={(e) => setRecipeSearchTerm(e.target.value)}
                    placeholder="Tarif adıyla ara"
                    className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-sm font-medium text-slate-700 outline-none transition focus:border-rose-400 dark:border-slate-700 dark:bg-[#0b0b0b] dark:text-slate-200"
                  />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => togglePreferenceFilter('sweet')}
                    className={`rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition ${preferenceFilters.sweet
                        ? 'bg-rose-500 text-white'
                        : 'bg-rose-50 text-rose-500 hover:bg-rose-100 dark:bg-rose-900/20 dark:text-rose-300 dark:hover:bg-rose-900/30'
                      }`}
                  >
                    Tatlı
                  </button>
                  <button
                    type="button"
                    onClick={() => togglePreferenceFilter('savory')}
                    className={`rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition ${preferenceFilters.savory
                        ? 'bg-amber-500 text-white'
                        : 'bg-amber-50 text-amber-600 hover:bg-amber-100 dark:bg-amber-900/20 dark:text-amber-300 dark:hover:bg-amber-900/30'
                      }`}
                  >
                    Tuzlu
                  </button>
                  <button
                    type="button"
                    onClick={() => togglePreferenceFilter('meze')}
                    className={`rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] transition ${preferenceFilters.meze
                        ? 'bg-emerald-500 text-white'
                        : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-300 dark:hover:bg-emerald-900/30'
                      }`}
                  >
                    İkramlık
                  </button>
                </div>
              </div>

              <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="rounded-full bg-slate-100 px-3 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-500 dark:bg-white/5 dark:text-slate-300">
                  {filteredPreferenceRecipes.length} tarif
                </div>
                <div className="rounded-full bg-slate-100 px-3 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-500 dark:bg-white/5 dark:text-slate-300">
                  {selectedRecipeIds.length} seçim
                </div>
                <button
                  type="button"
                  onClick={handleShoppingPreview}
                  disabled={!selectedRecipeIds.length || shoppingLoading}
                  className="rounded-full bg-rose-500 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {shoppingLoading ? 'Listeleniyor...' : `Eksikleri Listele (${selectedRecipeIds.length})`}
                </button>
                {!!shoppingPreview?.missingItems?.length && (
                  <button
                    type="button"
                    onClick={handleOpenShoppingScheduleModal}
                    className="rounded-full bg-sky-500 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-sky-600"
                  >
                    Alışveriş Listesine Gönder
                  </button>
                )}
              </div>

              {!filteredPreferenceRecipes.length ? (
                <div className="rounded-xl border border-dashed border-[#ece7e7] bg-white/70 px-4 py-6 text-sm font-medium text-slate-500 dark:border-gray-800 dark:bg-[#050505] dark:text-slate-400">
                  Bu filtrelerle eşleşen tarif bulunamadı. Filtreleri gevşetip tekrar deneyin.
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                  {filteredPreferenceRecipes.map((recipe) => (
                    <RecipeCard
                      key={`pref-${recipe.id || recipe.name}`}
                      recipe={recipe}
                      onCardClick={handleRecipeClick}
                      onToggleSelect={toggleRecipeSelection}
                      isSelected={selectedRecipeIds.includes(String(recipe.id))}
                      isDarkMode={isDarkMode}
                      userPortion={Number(responseAggregate?.confirmedGuestCount || setup.guestCount || 4)}
                    />
                  ))}
                </div>
              )}

              {shoppingPreview && (
                <div className="mt-6 rounded-[1.3rem] border border-[#ece7e7] bg-slate-50/70 p-4 dark:border-gray-800 dark:bg-[#0b0b0b]">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <h4 className="text-base font-black text-slate-900 dark:text-white">Alakart benzeri malzeme özeti</h4>
                    <span className="rounded-full bg-slate-100 px-3 py-1.5 text-[11px] font-black uppercase tracking-[0.14em] text-slate-500 dark:bg-white/5 dark:text-slate-300">
                      {shoppingPreview.selectedCount || 0} tarif
                    </span>
                  </div>
                  {!shoppingPreview.missingItems?.length ? (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-sm font-bold text-emerald-700 dark:border-emerald-900/30 dark:bg-emerald-950/10 dark:text-emerald-300">
                      Seçilen tariflerin malzemeleri stokta görünüyor.
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {shoppingPreview.missingItems.map((item) => (
                        <div
                          key={`pref-missing-${item.name}-${item.unit}`}
                          className="rounded-xl border border-[#ece7e7] bg-white p-4 dark:border-gray-800 dark:bg-[#050505]"
                        >
                          <p className="text-sm font-black text-slate-900 dark:text-white">{item.name}</p>
                          <p className="mt-1 text-xs font-bold text-rose-500">
                            {Number(item.quantity || 0).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} {item.unit || ''}
                          </p>
                          <p className="mt-2 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                            Tarifler: {Array.isArray(item.recipes) ? item.recipes.join(', ') : '-'}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

        </div>

        {!setupCompleted && (
          <div className="fixed inset-0 z-40 bg-black/75 backdrop-blur-sm">
            <style>{`
              .special-days-setup-modal {
                -ms-overflow-style: none;
                scrollbar-width: none;
              }
              .special-days-setup-modal::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            <div className="flex min-h-screen items-center justify-center p-2 pt-16 sm:p-4 sm:pt-20">
              <div className="special-days-setup-modal w-full max-w-[96vw] lg:max-w-5xl xl:max-w-[76rem] max-h-[calc(100dvh-4rem)] overflow-y-auto rounded-[1.35rem] border border-[#ece7e7] bg-white p-3.5 shadow-2xl sm:max-h-[calc(100vh-5.5rem)] sm:rounded-[2rem] sm:p-5 lg:px-7 dark:border-gray-800 dark:bg-[#050505]">
                <div className="mb-4">
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Etkinlik Girişi</p>
                  <h2 className="mt-1.5 text-xl font-black tracking-tight text-slate-900 dark:text-white sm:text-2xl">
                    Menüye erişmek için bilgileri doldurun
                  </h2>
                  <p className="mt-1.5 text-[13px] font-medium text-slate-500 dark:text-slate-400 sm:text-sm">
                    Kurulumu tamamlamadan tarif havuzu, alışveriş listesi ve event stoğu açılmayacak.
                  </p>
                </div>
                {setupGateForm}
              </div>
            </div>
          </div>
        )}

        <RecipeModalGateway
          modalVariant="smart"
          isOpen={recipeModalOpen}
          onClose={() => setRecipeModalOpen(false)}
          recipe={selectedRecipe}
          isDarkMode={isDarkMode}
          userPortion={Number(setup.guestCount || 4)}
          onSelect={handleModalRecipeSelect}
          isSelected={selectedRecipeIds.includes(String(selectedRecipe?.id || ''))}
          onMarkAsUsed={selectedRecipeIds.includes(String(selectedRecipe?.id || '')) ? handleMarkRecipeAsUsed : null}
        />

        {(!hasSpecialDaysAccess || purchaseModalOpen) && (
          <PremiumPurchaseModal
            isOpen={purchaseModalOpen}
            onClose={async () => {
              setPurchaseModalOpen(false);
              if (!pendingInvitePublish) return;
              await refreshSpecialDaysAccess();
              const published = await tryAutoPublishAfterPayment();
              if (!published) {
                setInviteSaveError('Ödeme onayı bekleniyor. Onay gelince davetiye otomatik yayınlanacak.');
              }
            }}
            preferredModuleKey="special_days"
            preferredModuleTitle="Özel Günler Mutfağı"
          />
        )}

        {surveyLinkModalOpen && !!surveyLink && (
          <div className="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
            <div className="w-full max-w-xl rounded-2xl border border-emerald-200 bg-white p-5 shadow-2xl dark:border-emerald-900/40 dark:bg-[#050505]">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-lg font-black text-slate-900 dark:text-white">Anket Linki Hazır</h3>
                <button
                  type="button"
                  onClick={handleSurveyFlowComplete}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Devam Et
                </button>
              </div>

              {surveyCloseAt && (
                <p className="mb-3 text-xs font-bold text-emerald-700 dark:text-emerald-300">
                  Bitiş: {new Date(surveyCloseAt).toLocaleString('tr-TR')}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <input
                  readOnly
                  value={surveyLink}
                  className="min-w-[240px] flex-1 rounded-xl border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 dark:border-emerald-900/30 dark:bg-[#0b0b0b] dark:text-slate-200"
                />
                <button
                  type="button"
                  onClick={async () => {
                    await navigator.clipboard.writeText(surveyLink);
                    setSurveyCopied(true);
                    setTimeout(() => setSurveyCopied(false), 1500);
                  }}
                  className="rounded-full bg-emerald-600 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-emerald-700"
                >
                  {surveyCopied ? 'Kopyalandı' : 'Linki Kopyala'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const msg = `Selam, özel gün davet ve anket linkimiz hazır. Katılım durumunu buradan iletebilirsin: ${surveyLink}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank', 'noopener,noreferrer');
                    handleSurveyFlowComplete();
                  }}
                  className="rounded-full bg-[#25D366] px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-[#1ebe5d]"
                >
                  WhatsApp ile Paylaş
                </button>
              </div>
            </div>
          </div>
        )}

        {shoppingScheduleModalOpen && (
          <div className="fixed inset-0 z-[145] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
            <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-800 dark:bg-[#050505]">
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-black text-slate-900 dark:text-white">Alışveriş Zamanı Onayı</h3>
                  <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
                    Alışverişi ne zaman yapacağınızı seçin. O saat için size push bildirim planlayalım.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setShoppingScheduleModalOpen(false);
                    setShoppingListError('');
                  }}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Kapat
                </button>
              </div>

              <NotificationSelector
                initialData={shoppingReminder}
                onChange={(payload) => {
                  setShoppingReminder(payload || { enabled: false, date: '', time: '09:00' });
                  if (shoppingListError) setShoppingListError('');
                }}
              />

              {shoppingListError && (
                <p className="mt-3 text-sm font-bold text-rose-500">{shoppingListError}</p>
              )}

              <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShoppingScheduleModalOpen(false);
                    setShoppingListError('');
                  }}
                  className="rounded-full border border-slate-300 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={handleCreateShoppingList}
                  disabled={shoppingListSubmitting}
                  className="rounded-full bg-sky-500 px-4 py-2 text-[11px] font-black uppercase tracking-[0.14em] text-white transition hover:bg-sky-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {shoppingListSubmitting ? 'Oluşturuluyor...' : 'Listeyi Oluştur'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

